// ---------------------------------------------------------------------------
// Context Enricher — resolves full block/page/flow JSON at send time.
//
// Called from chat-model-adapter before sending messages to LLM.
// Attaches targetJson with the resolved block or flow data so the LLM
// can make targeted modifications.
// ---------------------------------------------------------------------------

import type { ChatContext } from '@/store/chat-types'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { classifyDomain } from './domain-classifier'

/** Maximum number of UI elements before we truncate to metadata-only. */
const MAX_ELEMENTS = 50

/** Recursively count elements in a UI tree node. */
function countElements(node: unknown): number {
  if (!node || typeof node !== 'object') return 0
  let count = 1
  const children = (node as Record<string, unknown>).children
  if (Array.isArray(children)) {
    for (const child of children) count += countElements(child)
  }
  return count
}

/** Find an element by id in the UI tree. */
function findElementInTree(node: unknown, id: string): unknown | undefined {
  if (!node || typeof node !== 'object') return undefined
  const n = node as Record<string, unknown>
  if (n.id === id) return node
  const children = n.children
  if (Array.isArray(children)) {
    for (const child of children) {
      const found = findElementInTree(child, id)
      if (found) return found
    }
  }
  return undefined
}

/**
 * Enrich chat context with resolved JSON data at send time.
 *
 * - For selected blocks: attaches full block JSON (or metadata for large blocks)
 * - For selected flows: attaches flow definition JSON
 * - Classifies the domain from the last user message
 */
export function enrichChatContext(context: ChatContext, lastUserMessage?: string): ChatContext {
  const enriched = { ...context }

  // Classify domain from the last user message
  if (lastUserMessage) {
    const classification = classifyDomain(lastUserMessage)
    enriched.domain = classification.domain
    enriched.domainConfidence = classification.confidence
  }

  // Attach block JSON when a block is selected
  if (context.selectedBlockName) {
    const editorState = useEditorStore.getState()
    const block = (editorState as unknown as { blocks: Record<string, unknown> }).blocks?.[context.selectedBlockName]
    if (block && typeof block === 'object') {
      const blockObj = block as Record<string, unknown>
      const ui = blockObj.ui as Record<string, unknown> | undefined
      const elementCount = countElements(ui?.root)

      if (elementCount <= MAX_ELEMENTS) {
        enriched.targetJson = JSON.parse(JSON.stringify(block))
      } else {
        // For large blocks, include metadata only
        const meta: Record<string, unknown> = {
          meta: blockObj.meta,
          elementCount,
          _note: 'Block too large for full context. Selected element subtree only.',
        }
        // If a specific element is selected, include its subtree
        if (context.selectedElementId && ui?.root) {
          const subtree = findElementInTree(ui.root, context.selectedElementId)
          if (subtree) meta.selectedElement = JSON.parse(JSON.stringify(subtree))
        }
        enriched.targetJson = meta
      }
    }
  }

  // Attach flow JSON when a flow is selected (overrides block if both set)
  if (context.flowId) {
    const flowState = useFlowEditorStore.getState()
    const flow = flowState.flows.find((f) => f.id === context.flowId)
    if (flow) {
      enriched.targetJson = JSON.parse(JSON.stringify(flow)) as Record<string, unknown>
    }
  }

  return enriched
}
